import React, { useCallback, useEffect, useMemo } from 'react';

import usePersistedState from 'hooks/usePersistedState';
import { AuthContext } from 'constants/contexts';
import { triggerAnalyticEvent } from 'utils/analytics';
import useFetchedData from 'hooks/useFetchedData';
import { useHistory } from 'react-router-dom';
import RedirectProvider from 'components/RedirectProvider';
import { EventEmitter } from 'utils/event';
import { EVENTS } from 'constants/events';
import { usePushNotifications } from './use-push-notifications';

function AuthProvider({ children }) {
  const [user, setUser] = usePersistedState(null, AuthContext.STORAGE_KEY);
  const isLoggedIn = Boolean(user?.token && user?.tokenVerified);
  const { data: fetchedUser } = useFetchedData(isLoggedIn && 'user/profile/', ({ message } = {}) => message);
  const { saveRedirectPath } = RedirectProvider.useRedirect();
  const history = useHistory();
  const profileIsComplete = Boolean(user?.name && user?.email);

  const redirectAfterLoggedIn = useCallback((path) => {
    path = path || history.location.pathname;
    if (isLoggedIn && profileIsComplete) {
      history.push(path);
    } else if (isLoggedIn && !profileIsComplete) {
      setTimeout(() => {
        saveRedirectPath(path);
        history.push('/signup');
      }, 10);
    } else {
      setTimeout(() => {
        saveRedirectPath(path);
        history.push('/login');
      }, 10);
    }
  }, [isLoggedIn, profileIsComplete]);

  useEffect(() => {
    if (fetchedUser) {
      setUser((u) => ({
        ...u,
        ...fetchedUser,
      }));
    }
  }, [fetchedUser, setUser]);

  usePushNotifications({ isLoggedIn });

  const contextValue = useMemo(() => ({
    user,
    logout() {
      setUser(null);
      EventEmitter.fire(EVENTS.SIGN_OUT);
      triggerAnalyticEvent('logout');
    },
    redirectAfterLoggedIn,
    setUser(u) { setUser((current) => ({ ...current, ...u })); },
    isLoggedIn,
    profileIsComplete,
  }), [user, setUser, profileIsComplete]);

  return (
    <AuthContext.Provider
      value={contextValue}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
